export interface SurgeonBillingRule {
	id: number | string;
	user_id?: number;
	surgeon_id?: number | string;
	hospital_id?: number | string | null;
	start_date?: string | null;
	end_date?: string | null;
	default_oop?: string | number | null;
	ifc_required?: boolean;
	hospital?: {
		id?: number | string | null;
		name?: string;
	} | null;
}

type EventLike = {
	date_of_service?: string | null;
	hospital_id?: number | string | null;
	hospital?: {
		id?: number | string | null;
	} | null;
	ifc_required?: boolean;
	[key: string]: any;
};

type SessionLike = {
	date_of_service?: string | null;
	date?: string | null;
	surgeon_id?: number | string | null;
	surgeon?: {
		id?: number | string | null;
	} | null;
	hospital_id?: number | string | null;
	hospital?: {
		id?: number | string | null;
	} | null;
	ifc_required?: boolean;
	events?: EventLike[];
	[key: string]: any;
};

type RulesInput =
	| SurgeonBillingRule[]
	| Record<string | number, SurgeonBillingRule[]>;

const normaliseDate = (value?: string | null): string | null => {
	if (!value) return null;

	const date = String(value).slice(0, 10);

	return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

const normaliseId = (value?: number | string | null): number | null => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	const id = Number(value);

	return Number.isFinite(id) ? id : null;
};

const getRuleHospitalId = (rule: SurgeonBillingRule): number | null => {
	return normaliseId(rule.hospital_id ?? rule.hospital?.id ?? null);
};

const isTimelineRule = (rule: SurgeonBillingRule): boolean => {
	return normaliseDate(rule.start_date) !== null;
};

const dateFallsWithinRule = (
	eventDate: string,
	rule: SurgeonBillingRule,
): boolean => {
	const startDate = normaliseDate(rule.start_date);
	const endDate = normaliseDate(rule.end_date);

	if (!startDate) return false;

	if (eventDate < startDate) {
		return false;
	}

	if (endDate && eventDate > endDate) {
		return false;
	}

	return true;
};

const getRulesForSurgeon = (
	rules: RulesInput,
	surgeonId: number | null,
): SurgeonBillingRule[] => {
	if (surgeonId === null) {
		return [];
	}

	if (Array.isArray(rules)) {
		return rules.filter((rule) => {
			const ruleSurgeonId = normaliseId(rule.surgeon_id);

			return ruleSurgeonId === null || ruleSurgeonId === surgeonId;
		});
	}

	const surgeonRules = rules[surgeonId] ?? rules[String(surgeonId)];

	return Array.isArray(surgeonRules) ? surgeonRules : [];
};

export const resolveSurgeonBillingRule = (
	event: EventLike | null | undefined,
	rules: SurgeonBillingRule[] = [],
	selectedSession?: SessionLike | null,
): SurgeonBillingRule | null => {
	if (!Array.isArray(rules) || rules.length === 0) {
		return null;
	}

	const eventDate = normaliseDate(
		event?.date_of_service ??
			selectedSession?.date_of_service ??
			selectedSession?.date ??
			null,
	);

	const eventHospitalId = normaliseId(
		event?.hospital_id ??
			event?.hospital?.id ??
			selectedSession?.hospital_id ??
			selectedSession?.hospital?.id ??
			null,
	);

	const timelineRules = rules.filter(isTimelineRule).sort((a, b) => {
		const aStart = normaliseDate(a.start_date) ?? "";

		const bStart = normaliseDate(b.start_date) ?? "";

		return bStart.localeCompare(aStart);
	});

	if (eventDate) {
		if (eventHospitalId !== null) {
			const timelineLocationRule = timelineRules.find((rule) => {
				const ruleHospitalId = getRuleHospitalId(rule);

				return (
					ruleHospitalId !== null &&
					ruleHospitalId === eventHospitalId &&
					dateFallsWithinRule(eventDate, rule)
				);
			});

			if (timelineLocationRule) {
				return timelineLocationRule;
			}
		}

		const timelineGeneralRule = timelineRules.find((rule) => {
			const ruleHospitalId = getRuleHospitalId(rule);

			return (
				ruleHospitalId === null && dateFallsWithinRule(eventDate, rule)
			);
		});

		if (timelineGeneralRule) {
			return timelineGeneralRule;
		}
	}

	if (eventHospitalId !== null) {
		const locationRule = rules.find((rule) => {
			if (isTimelineRule(rule)) {
				return false;
			}

			return getRuleHospitalId(rule) === eventHospitalId;
		});

		if (locationRule) {
			return locationRule;
		}
	}

	return null;
};

export const applySurgeonBillingRulesToSessions = (
	sessions: SessionLike[] = [],
	rules: RulesInput = [],
): SessionLike[] => {
	if (!Array.isArray(sessions)) {
		return [];
	}

	return sessions.map((session) => {
		const surgeonId = normaliseId(
			session.surgeon_id ?? session.surgeon?.id ?? null,
		);

		const surgeonRules = getRulesForSurgeon(rules, surgeonId);

		const sessionRule = resolveSurgeonBillingRule(
			null,
			surgeonRules,
			session,
		);

		const events = Array.isArray(session.events)
			? session.events.map((event) => {
					const matchedRule = resolveSurgeonBillingRule(
						event,
						surgeonRules,
						session,
					);

					return {
						...event,
						default_oop: matchedRule?.default_oop ?? null,
						ifc_required:
							event.ifc_required ??
							matchedRule?.ifc_required ??
							false,
						surgeonBillingRule: matchedRule ?? null,
					};
				})
			: session.events;

		return {
			...session,
			default_oop: sessionRule?.default_oop ?? null,
			ifc_required:
				session.ifc_required ?? sessionRule?.ifc_required ?? false,
			surgeonBillingRule: sessionRule ?? null,
			events,
		};
	});
};
